import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2 } from 'lucide-react'
import { getAuthHeaders } from '@/contexts/AuthContext'

// DEMO MODE: Use mock data (set to false once API authentication is configured)
const USE_MOCK_DATA = false;

interface FleetStats {
  active: number
  idle: number
  alerts: number
  offline: number
  total: number
}

// Mock data for demo
const mockStats: FleetStats = {
  active: 35,
  idle: 8,
  alerts: 3,
  offline: 5,
  total: 48
};

export function FleetOverview() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['fleet-stats'],
    queryFn: async () => {
      if (USE_MOCK_DATA) return mockStats;
      const res = await fetch('/api/fleet/stats', {
        headers: getAuthHeaders()
      })
      if (!res.ok) throw new Error('Failed to fetch fleet stats')
      return res.json() as Promise<FleetStats>
    },
    refetchInterval: USE_MOCK_DATA ? false : 30000 // Refresh every 30 seconds
  })

  if (isLoading) {
    return (
      <Card className="w-80">
        <CardHeader>
          <CardTitle className="text-base font-medium">Fleet Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-80 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium">Fleet Overview</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Active Vehicles */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-green-500" />
            <span className="text-sm font-medium">Active</span>
          </div>
          <span className="text-2xl font-bold">{stats?.active || 0}</span>
        </div>

        {/* Idle Vehicles */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-yellow-500" />
            <span className="text-sm font-medium">Idle</span>
          </div>
          <span className="text-2xl font-bold">{stats?.idle || 0}</span>
        </div>

        {/* Alerts */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-red-500" />
            <span className="text-sm font-medium">Alerts</span>
          </div>
          <span className="text-2xl font-bold">{stats?.alerts || 0}</span>
        </div>

        {/* Offline Vehicles */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-gray-500" />
            <span className="text-sm font-medium">Offline</span>
          </div>
          <span className="text-2xl font-bold">{stats?.offline || 0}</span>
        </div>

        {/* Divider */}
        <div className="border-t pt-3 mt-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Total Vehicles</span>
            <span className="text-xl font-bold">{stats?.total || 0}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
